import axios from "axios";
import moment from 'moment';
import AdminService from "@/services/admin.service.js";

/**
 * @classdesc Wrapper d'un module
 * @kind class
 * @export
 * @prop {int} id - Id du module
 * @prop {string} nom  - Nom du module
 * @prop {string} base  - Trigramme du module
 * @prop {int} id_evenement  - Id de l'évenement
 * @prop {string} gestion  - Organisation en charge de la gestion
 * @prop {date} date_debut_inscription - Date de début du module
 * @prop {time} heure_debut_inscription - Heure de début du module
 * @prop {date} date_fin_inscription - Date de fin du module
 * @prop {time} heure_fin_inscription - Heure de fin du module
 * @prop {string} status - Statut des inscriptions du module
 * @prop {string} url_flyer - Url du flyer du module
 * @prop {array} metadatas - Métadonnées du module
 * @prop {Evenement} evenement - Information de l'évenement associé
 * @prop {int} nb_inscrits - Nombre de participants total
 * @class Module
 */
export default class Module {

    id = "";
    nom = "";
    base = "";
    id_evenement;
    gestion = "sportips";
    date_debut_inscription = moment().format("YYYY-MM-DD");
    heure_debut_inscription = moment().format("HH:mm");
    date_fin_inscription = moment().add(1, "d").format("YYYY-MM-DD");
    heure_fin_inscription = moment().format("HH:mm");
    status = "";
    url_flyer = "";
    url_reglement = "";
    url_background = "";
    metadatas = [];
    evenement;
    nb_inscrits;

    /**
     *  Initialise un module
     *
     * @constructs
     * @memberof Module
     */
    constructor() { }

    /**
     * Appel API pour charger le module
     *
     * @return {boolean} success
     * @async
     * @memberof Module
     */
    async fetch() {
        let res = await axios.get("/public/module/get.php?base=" + this.base);
        if (res.data.error) {
            console.log(res.data.error);
            return await false;
        } else {
            this.set(res.data);
            return await true;
        }
    }


    /**
     * Défini le module à partir d'un jeu de données
     *
     * @param {Object} data
     * @memberof Module
     */
    set(data) {
        for (const [key, value] of Object.entries(data)) {
            if (this.hasOwnProperty(key)) {
                this[key] = value;
            }
        }
        if (this.url_background != "") {
            if (document.getElementsByClassName("v-application")[0]) {
                document.getElementsByClassName("v-application")[0].style.background = "url('" + this.url_background + "')";
            }

        }
    }

    /**
     *
     * Verify if the module has metadata with the given name.
     * @param {*} name
     * @return {*} 
     * @memberof Module
     */
    has_metadata(name) {
        if (name == null || name == undefined) {
            return false;
        }
        if (this.metadatas == null || this.metadatas == undefined) {
            return false;
        }
        if (typeof name !== "string") {
            return false;
        }
        if (typeof this.metadatas !== "object") {
            return false;
        }
        for (const [key, value] of Object.entries(this.metadatas)) {
            if (key == name) {
                return true;
            }
        }
        return false;
    }



    /**
     * This method is used to get metadata of the product.
     * @param name - Name of the metadata.
     * @returns {boolean} - Returns metadata if found otherwise false.
     */

    get_metadata(name) {
        for (const [key, value] of Object.entries(this.metadatas)) {
            if (key == name) {
                return value;
            }
        }
        return false;
    }

    async get_extra_buttons_accueil() {
        for (const [key, value] of Object.entries(this.metadatas)) {
            if (key.includes("btn_accueil_")) {
                let buttons = await this.fetch_buttons();
                return buttons;

            }
        }
    }

    async fetch_buttons() {
        let buttons = [];
        let res = await axios.get("/metadatas/get.php?resource_type=module&id_resource=" + this.id);
        if (res.data.error) {
            return await this.buttons;
        } else {
            for (let i = 0; i < res.data.length; i++) {
                if (res.data[i].nom.includes('btn_accueil_')) {
                    buttons.push(res.data[i]);
                }
            }
            return await buttons;

        }
    }

    /**
     * Vérifie si le module accepte les PPS
     * @returns {boolean} Vrai si le module accepte les PPS, faux sinon
     * @memberof Module
     */
    accept_pps() {
        // Vérifie si le module remplace les CM par des PPS
        const is_module_replace_cm_by_pps = this.get_metadata("replace_cm_by_pps") == "1";

        // Vérifie si le module n'est pas un module FFA et ne remplace pas les CM par des PPS
        if (this.get_metadata("is_ffa_module") != "1" && !is_module_replace_cm_by_pps) {
            return false;
        }

        // Vérifie si le module n'a pas de PPS et ne remplace pas les CM par des PPS
        if (this.get_metadata("has_pps") != "1" && !is_module_replace_cm_by_pps) {
            return false;
        }

        // Vérifie si la date de fin de l'événement est avant le 1er avril 2024
        if (moment(this.evenement.date_fin).isBefore("2024-04-01")) {
            return false;
        }

        // Vérifie si le module est un module FFA avec PPS
        const is_module_ffa_with_pps = this.get_metadata("is_ffa_module") == "1" && this.get_metadata("has_pps") == "1";

        // Retourne vrai si le module est un module FFA avec PPS ou s'il remplace les CM par des PPS
        return is_module_ffa_with_pps || is_module_replace_cm_by_pps;
    }




    is_complet() {
        if (this.metadatas.limite_participants && this.metadatas.limite_participants == "1") {
            if (this.metadatas.type_limite_participants == "Globale" && this.metadatas.nb_limite_globale) {
                if (parseInt(this.nb_inscrits) >= parseInt(this.metadatas.nb_limite_globale)) {
                    return true;
                }
            }
        }
        return false;
    }

    /* 
        Vérifie si les inscriptions sont ouvertes pour le module
    */
    is_inscription_open() {
        let now = moment();
        let ouverture_inscription = moment(this.date_debut_inscription + " " + this.heure_debut_inscription);
        let fermeture_inscription = moment(this.date_fin_inscription + " " + this.heure_fin_inscription);

        if (now.isBefore(ouverture_inscription)) {
            return false;

        }
        if (now.isAfter(fermeture_inscription)) {
            return false;
        }
        return true;
    }

    async upload_reglement(file) {
        let response = { success: false, message: "" };
        if (!file || file.size > 2000000) {
            response.message = "Le fichier ne doit pas dépasser 2Mo";
            return response;
        }
        var data = new FormData();
        data.append("file", file, file.name);
        data.append("id_event", this.evenement.id);
        let request = await AdminService.uploadReglement(data);
        if (request.url) {
            this.url_reglement = request.url;
            response.success = true;
            response.message = "Le réglement a bien été mis à jour";
        }
        else {
            response.message = request.error;
        }
        return response;

    }

    async upload_flyer(file) {
        let response = { success: false, message: "" };

        if (!file || file.size > 2000000) {
            response.message = "Le fichier ne doit pas dépasser 2Mo";
            return response;
        }
        var data = new FormData();
        data.append("file", file, file.name);
        data.append("id_event", this.evenement.id);
        let request = await AdminService.uploadFlyer(data);
        if (request.url) {
            this.url_flyer = request.url;
            response.success = true;
            response.message = "Le flyer a bien été mis à jour";
        }
        else {
            response.message = request.error;
        }
        return response;

    }

    async update_config() {
        let res = await AdminService.updateModuleConfig(this);
        return res;
    }

}
